import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const API_BASE = '/api';

function ChatWindow({ conversation, selectedModel, selectedPrompt, onPromptUsed, onMessageSent }) {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [streamingMessage, setStreamingMessage] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [abortController, setAbortController] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const fileInputRef = useRef(null);
  const [didInitialScroll, setDidInitialScroll] = useState(false);

  useEffect(() => {
    if (conversation) {
      setMessages(conversation.messages || []);
      setStreamingMessage('');
      setDidInitialScroll(false);
    } else {
      setMessages([]);
    }
  }, [conversation]);

  useEffect(() => {
    // Jump to bottom on first load for a conversation (instant),
    // use smooth scrolling for subsequent updates (new messages/streaming)
    if (!didInitialScroll) {
      scrollToBottom(false);
      setDidInitialScroll(true);
    } else {
      scrollToBottom(true);
    }
  }, [messages, streamingMessage]);

  useEffect(() => {
    if (selectedPrompt) {
      setInputMessage(selectedPrompt.content);
      if (onPromptUsed) {
        onPromptUsed();
      }
    }
  }, [selectedPrompt]);

  const scrollToBottom = (smooth = true) => {
    const container = messagesContainerRef.current;
    if (!container) return;
    if (smooth && container.scrollTo) {
      container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
    } else {
      container.scrollTop = container.scrollHeight;
    }
  };

  const cancelStreaming = () => {
    if (abortController) {
      abortController.abort();
      setAbortController(null);
    }
    setIsLoading(false);
    setIsStreaming(false);
    setStreamingMessage('');
  };

  const sendMessage = async () => {
    if ((!inputMessage.trim() && !selectedImage) || !conversation) return;

    const userMessage = inputMessage;
    const imageToSend = selectedImage;
    const imagePreviewToShow = imagePreview;
    
    // Clear input immediately
    setInputMessage('');
    setSelectedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    
    // Immediately show the user's message (with image preview if present)
    const tempUserMessage = {
      role: 'user',
      content: userMessage,
      image_preview: imagePreviewToShow,
      created_at: new Date().toISOString(),
      id: `temp-${Date.now()}`
    };
    setMessages(prev => [...prev, tempUserMessage]);
    
    setIsLoading(true);
    setIsStreaming(true);
    setStreamingMessage('');

    try {
      // Create abort controller for cancellation
      const controller = new AbortController();
      setAbortController(controller);

      // Prepare request - use FormData if image is present
      let response;
      
      if (imageToSend) {
        // Use FormData for image upload
        const formData = new FormData();
        formData.append('content', userMessage || 'Analyze this image');
        formData.append('model', selectedModel || 'llava');
        formData.append('stream', 'true');
        formData.append('image', imageToSend);
        
        response = await fetch(
          `${API_BASE}/conversations/${conversation.id}/send_message/`,
          {
            method: 'POST',
            headers: {
              'X-CSRFToken': getCookie('csrftoken'),
            },
            credentials: 'include',
            signal: controller.signal,
            body: formData
          }
        );
      } else {
        // Regular text-only message
        response = await fetch(
          `${API_BASE}/conversations/${conversation.id}/send_message/`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': getCookie('csrftoken'),
            },
            credentials: 'include',
            signal: controller.signal,
            body: JSON.stringify({
              content: userMessage,
              model: selectedModel,
              stream: true
            })
          }
        );
      }

      if (!response.ok) {
        throw new Error('Failed to send message');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullContent = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              if (data.content) {
                fullContent += data.content;
                setStreamingMessage(fullContent);
              }
              if (data.done) {
                // Stream complete - add the final message directly instead of reloading
                setIsStreaming(false);
                
                // Create the assistant message from the streamed content
                const assistantMessage = {
                  role: 'assistant',
                  content: fullContent,
                  created_at: new Date().toISOString(),
                  id: `streamed-${Date.now()}`,
                  model_used: selectedModel
                };
                
                // Add to messages immediately for smooth transition
                setMessages(prev => [...prev, assistantMessage]);
                setStreamingMessage('');
                
                // Reload in background to get the actual saved message with correct ID
                setTimeout(async () => {
                  const updatedConv = await axios.get(`${API_BASE}/conversations/${conversation.id}/`);
                  setMessages(updatedConv.data.messages);
                  onMessageSent();
                }, 100);
              }
              if (data.error) {
                throw new Error(data.error);
              }
            } catch (e) {
              // Skip invalid JSON
            }
          }
        }
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('Streaming cancelled by user');
        // Reload to get the actual state
        const updatedConv = await axios.get(`${API_BASE}/conversations/${conversation.id}/`);
        setMessages(updatedConv.data.messages);
      } else {
        console.error('Error sending message:', error);
        alert('Error sending message. Make sure Ollama is running.');
        // Remove temp message on error
        setMessages(prev => prev.filter(m => m.id !== tempUserMessage.id));
      }
    } finally {
      setIsLoading(false);
      setIsStreaming(false);
      setStreamingMessage('');
      setAbortController(null);
    }
  };

  function getCookie(name) {
    let cookieValue = null;
    if (document.cookie && document.cookie !== '') {
      const cookies = document.cookie.split(';');
      for (let i = 0; i < cookies.length; i++) {
        const cookie = cookies[i].trim();
        if (cookie.substring(0, name.length + 1) === (name + '=')) {
          cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
          break;
        }
      }
    }
    return cookieValue;
  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }
      setSelectedImage(file);
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (!conversation) {
    return (
      <div className="chat-window-empty">
        <div className="empty-state-large">
          <h2>Welcome to AI Chat</h2>
          <p>Select a conversation from the sidebar or create a new one to get started.</p>
          <div className="features">
            <div className="feature">
              <span className="icon"><i className="fas fa-comments"></i></span>
              <h3>Chat with AI</h3>
              <p>Have streaming conversations with Ollama AI models</p>
            </div>
            <div className="feature">
              <span className="icon"><i className="fas fa-tools"></i></span>
              <h3>Tool Library</h3>
              <p>Create reusable AI tools with custom instructions</p>
            </div>
            <div className="feature">
              <span className="icon"><i className="fas fa-key"></i></span>
              <h3>API Access</h3>
              <p>Automate tasks with REST API and custom tools</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="chat-window">
      <div className="chat-header">
        <h2>{conversation.title || `Conversation ${conversation.id}`}</h2>
        <div className="chat-info">
          {selectedModel && <span className="model-badge">{selectedModel}</span>}
          <span className="message-count">{messages.length} messages</span>
        </div>
      </div>

      <div className="messages-container" ref={messagesContainerRef}>
        {messages.map((message, index) => (
          <div key={index} className={`message ${message.role}`}>
            <div className="message-header">
              <span className="role">{message.role === 'user' ? 'You' : 'AI'}</span>
              <span className="timestamp">{formatTimestamp(message.created_at)}</span>
            </div>
            <div className="message-content">
              {(message.image_preview || message.image_data) && (
                <img 
                  src={message.image_preview || `data:image/png;base64,${message.image_data}`} 
                  alt="Uploaded" 
                  className="message-image" 
                />
              )}
              <div className="md-body">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {message.content || ''}
                </ReactMarkdown>
              </div>
            </div>
            {message.model_used && (
              <div className="message-footer">
                <span className="model-used">Model: {message.model_used}</span>
              </div>
            )}
          </div>
        ))}
        
        {isLoading && !streamingMessage && (
          <div className="message assistant">
            <div className="message-header">
              <span className="role">AI</span>
            </div>
            <div className="message-content loading">
              <span className="loading-dots">Thinking...</span>
            </div>
          </div>
        )}
        
        {streamingMessage && (
          <div className="message assistant streaming">
            <div className="message-header">
              <span className="role">AI</span>
              <span className="streaming-indicator"><i className="fas fa-circle"></i> Streaming...</span>
            </div>
            <div className="message-content">
              <div className="md-body">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {streamingMessage}
                </ReactMarkdown>
              </div>
              <span className="cursor-blink"><i className="fas fa-square"></i></span>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      <div className="input-container">
        {imagePreview && (
          <div className="image-preview-container">
            <img src={imagePreview} alt="Preview" className="image-preview" />
            <button onClick={removeImage} className="btn-remove-image" title="Remove image">
              <i className="fas fa-times"></i>
            </button>
          </div>
        )}
        <textarea
          value={inputMessage}
          onChange={(e) => setInputMessage(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Type your message... (Enter to send, Shift+Enter for new line)"
          disabled={isLoading}
        />
        <div className="input-actions">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleImageSelect}
            style={{ display: 'none' }}
          />
          {isLoading && (
            <button
              onClick={cancelStreaming}
              className="btn btn-cancel"
            >
              <i className="fas fa-times"></i> Cancel
            </button>
          )}
          <button
            onClick={sendMessage}
            disabled={(!inputMessage.trim() && !selectedImage) || isLoading}
            className="btn btn-primary"
          >
            {isLoading ? 'Sending...' : 'Send'}
          </button>
          <button
            onClick={() => fileInputRef.current?.click()}
            className="btn btn-secondary"
            disabled={isLoading}
            title="Upload image"
          >
            <i className="fas fa-camera"></i> Image
          </button>
        </div>
      </div>
    </div>
  );
}

export default ChatWindow;
